import * as types from '../actionTypes';
import api from '../../Services/api';
import logger from '../../Services/logger';
import { quartoTemCafe } from '../../Constants/quote';
import * as mockF2 from '../../Mocks/quotesFase2';
import { FEATURE_FLAGS } from '../../Config/environment';
import env from '../../Config/env';

/**
 * Thunks da feature de Cotação — integrados à API real (`/api/v1/quote/*`,
 * autenticados via cliente `api`/Bearer). Requer o direito `listQuotes`.
 *
 * O backend faz o escopo de permissão (Master vê tudo; agência vê a sua;
 * `viewOwnQuotesOnly` restringe ao próprio), congela o câmbio na criação e
 * calcula precoBRL/noites. Aqui só montamos os payloads e normalizamos a
 * resposta para o shape que a UI já consome (compat com a fase mock).
 */

// --- Normalização API -> shape da UI (mantém a UI inalterada) ---------------
// O backend RETORNA o item FLATTENED (checkIn, paxAdultos, hotelNome, hotelFoto,
// quartoNome...). A UI consome NESTED (periodo/pax/hotel/quarto). Mapeamos aqui.
const normalizeItem = (it) => {
	if (!it) return it;
	// Se já vier nested (ex.: item recém-montado), mantém.
	if (it.periodo || it.hotel) {
		const quarto = { ...(it.quarto || {}) };
		if (quarto.cafe === undefined && it.temCafe !== undefined) quarto.cafe = !!it.temCafe;
		return { ...it, quarto };
	}
	return {
		id: it.id,
		QuoteId: it.QuoteId,
		// Fase 2: agrupamento por trecho (ponto de decisão do cliente).
		trechoId: it.trechoId ?? null,
		periodo: { checkIn: it.checkIn, checkOut: it.checkOut, noites: it.noites },
		noites: it.noites,
		// criancasIdades é usado no passo de reserva (Fase 2) — o QuoteItem já persiste.
		pax: { adultos: it.paxAdultos ?? 0, criancas: it.paxCriancas ?? 0, criancasIdades: it.criancasIdades ?? it.paxCriancasIdades ?? [] },
		hotel: {
			id: it.hotelId,
			nome: it.hotelNome,
			cidade: it.hotelCidade,
			pais: it.hotelPais,
			foto: it.hotelFoto ?? null,
		},
		quarto: {
			nome: it.quartoNome, tarifa: it.tarifa, regime: it.regime, cafe: !!it.temCafe,
			// identificadores técnicos do fornecedor (re-cotação Fase 2).
			roomId: it.roomId ?? null, ratePlanCode: it.ratePlanCode ?? null,
		},
		temCafe: !!it.temCafe,
		precoUSD: it.precoUSD,
		precoBRL: it.precoBRL,
		// Congelamento POR RESERVA (BE 2026-08-07): quando a reserva deste item foi paga, o
		// BRL para de acompanhar o câmbio do dia e fica na taxa daquele pagamento. Os dois
		// campos existem para a tela EXPLICAR isso — sem eles, o valor parado parece erro.
		// null enquanto não pago.
		cambioUsdBrlPago: it.cambioUsdBrlPago ?? null,
		cambioCongeladoEm: it.cambioCongeladoEm ?? null,
		// Atalho booleano que o BE manda junto (2026-08-08): serve quando só interessa saber SE
		// congelou, sem a taxa. Mantido como fallback — a UI prefere mostrar a taxa quando existe.
		valorCongelado: it.valorCongelado ?? (it.cambioUsdBrlPago != null),
		pagoNoLocal: it.pagoNoLocal,
		// backend devolve 0/1 — normaliza p/ boolean (a UI marca indisponível com === false).
		disponivel: it.disponivel == null ? undefined : !!it.disponivel,
		// token do link público de visualização do hotel (null se item antigo sem os ids).
		hotelViewToken: it.hotelViewToken,
	};
};

const normalizeQuote = (q) => {
	if (!q) return q;
	// A UI consome `itens` (PT). Backend devolve `QuoteItems`.
	const itens = (q.QuoteItems || q.itens || q.items || []).map(normalizeItem);
	return {
		...q,
		itens,
		// createdBy.{nome,agencyName} e criadoEm são lidos pela UI.
		// agencyName agora usa `agenciaNome` (já resolve "Não definido" -> razão social,
		// ao vivo); fallback p/ o snapshot antigo `agencyName`.
		createdBy: { nome: q.createdByName, agencyName: q.agenciaNome || q.agencyName },
		criadoEm: q.createdAt,
		atualizadoEm: q.updatedAt,
		cambioSnapshot: q.cambioSnapshotUsdBrl != null
			? { usdBrl: q.cambioSnapshotUsdBrl, data: q.cambioSnapshotData }
			: null,
	};
};

// Junta cada reserva (shape do BE: {bookingId, trechoId, itemId, codigoReserva, status,
// prazoPagamento, hotelViewToken, hotelNome}) com o item da cotação (por itemId) para
// preencher os campos que o painel lê como STRING (hotel/quarto) + periodo/precoBRL/foto.
// Coage o câmbio p/ número (o BE pode devolver usdBrl como string "5.42").
const coerceCambio = (c) => (c && c.usdBrl != null ? { ...c, usdBrl: Number(c.usdBrl) } : (c || null));

/**
 * Normaliza `reservas[]` para o shape que os componentes consomem.
 *
 * @param {boolean} publico  payload da página pública (BE 2026-08-04): superfície mínima
 *   `{bookingId, trechoId, codigoReserva, status, hotelNome, cidade, pais, checkIn, checkOut}`,
 *   deliberadamente SEM preço/custo/markup porque a página abre por link sem auth.
 *   Nesse modo NÃO completamos com dados do item — inventar preço aqui furaria a
 *   proteção do BE e ainda poderia divergir do valor realmente reservado.
 */
const enriquecerReservas = (reservas, itens = [], { publico = false } = {}) => {
	if (!Array.isArray(reservas)) return [];
	return reservas.map((r) => {
		const it = publico ? null : (itens.find((x) => x.id === r.itemId || x.id === r.QuoteItemId) || null);
		// Público manda checkIn/checkOut soltos; agência manda `periodo`.
		const periodo = r.periodo || (r.checkIn ? { checkIn: r.checkIn, checkOut: r.checkOut } : it?.periodo) || null;
		return {
			...r,
			id: r.id || r.bookingId || null,
			hotel: r.hotel || r.hotelNome || it?.hotel?.nome || null,
			quarto: r.quarto || it?.quarto?.nome || null,
			regime: r.regime || it?.quarto?.regime || null,
			temCafe: r.temCafe ?? it?.temCafe ?? false,
			periodo,
			precoBRL: r.precoBRL ?? it?.precoBRL ?? null,
			foto: r.foto || it?.hotel?.foto || null,
			hotelViewToken: r.hotelViewToken || it?.hotelViewToken || null,
		};
	});
};

// Normaliza a cotação PÚBLICA (GET /public/quote/:token) p/ o shape que a página
// pública consome (QuoteSelectionView). Aceita { quote } ou o objeto direto. Reusa
// normalizeItem + enriquecerReservas; não aplica a normalização de agência (createdBy etc.).
export const normalizePublicQuote = (raw) => {
	// Desembrulha envelopes comuns do BE: {quote}, {data:{quote}}, {data:{...}}, ou o objeto direto.
	const q = raw?.quote || raw?.data?.quote || raw?.data || raw;
	if (!q) return q;
	const itens = (q.QuoteItems || q.itens || q.items || []).map(normalizeItem);
	return {
		...q,
		itens,
		trechos: Array.isArray(q.trechos) ? q.trechos : [],
		selecaoCliente: q.selecaoCliente || {},
		reservas: enriquecerReservas(q.reservas, itens, { publico: true }),
		cambio: coerceCambio(q.cambio || (q.cambioSnapshotUsdBrl != null ? { usdBrl: q.cambioSnapshotUsdBrl, data: q.cambioSnapshotData } : null)),
	};
};

// Normalização EXTRA para cotações Fase 2. Base legada + garante o shape que os
// componentes F2 consomem. Onde o contrato do BE ainda não está 100% fechado, mapeia
// com fallback + TODO. `reservasExtra` (resposta do proceed) entra como fallback quando
// o retrieve não devolve as reservas.
const normalizeQuoteF2 = (q, reservasExtra) => {
	const base = normalizeQuote(q);
	if (!base) return base;
	return {
		...base,
		// Link público: o BE guarda `publicToken` (reusável entre reenvios). A UI usa `quote.token`.
		token: q.publicToken || q.public_token || q.publicUrlToken || q.token || base.token || null,
		// Câmbio do dia aplicado aos valores. GET público devolve `cambio {usdBrl,data,validoAte}`;
		// no retrieve da agência pode vir como snapshot. TODO(BE): confirmar o campo final.
		cambio: coerceCambio(q.cambio || base.cambioSnapshot),
		// Coleções/objetos F2 — defaults seguros (evita crash se algum vier ausente).
		// TODO(BE): confirmar nomes (trechos/selecaoCliente/reservas/eventos/perda/contato) e o
		// shape de `reservas` (hotel/quarto/periodo/status/prazoPagamento/hotelViewToken/codigoReserva).
		trechos: Array.isArray(q.trechos) ? q.trechos : [],
		selecaoCliente: q.selecaoCliente || {},
		// reservas do retrieve; se ausentes, usa as do proceed (reservasExtra). Enriquece com os itens.
		reservas: enriquecerReservas(
			Array.isArray(q.reservas) && q.reservas.length ? q.reservas : (reservasExtra || q.reservas),
			base.itens,
		),
		eventos: Array.isArray(q.eventos) ? q.eventos : [],
		perda: q.perda ?? null,
		contato: q.contato ?? null,
		// status / enviadaEm / visualizadoEm / aprovadaEm / agencyViewToken sobrevivem via ...q (base).
	};
};

// Monta o `item` no CONTRATO OFICIAL do backend a partir do item da UI.
const toApiItem = (item) => ({
	periodo: { checkIn: item?.periodo?.checkIn, checkOut: item?.periodo?.checkOut },
	pax: {
		adultos: item?.pax?.adultos ?? 0,
		criancas: item?.pax?.criancas ?? 0,
		criancasIdades: item?.pax?.criancasIdades ?? [],
	},
	// origin (hyperguest|priceline) — necessário p/ re-cotar valores/disponibilidade.
	origin: item?.origin ?? null,
	hotel: {
		id: item?.hotel?.id,
		nome: item?.hotel?.nome,
		cidade: item?.hotel?.cidade,
		pais: item?.hotel?.pais,
		foto: item?.hotel?.foto ?? null,
	},
	quarto: {
		nome: item?.quarto?.nome,
		tarifa: item?.quarto?.tarifa,
		regime: item?.quarto?.regime,
		// identificadores técnicos do fornecedor (p/ re-cotação).
		roomId: item?.quarto?.roomId ?? null,
		ratePlanCode: item?.quarto?.ratePlanCode ?? null,
	},
	temCafe: quartoTemCafe(item?.quarto),
	precoUSD: item?.precoUSD ?? 0,
	pagoNoLocal: item?.pagoNoLocal ?? 0,
});

// SUDO: master operando "como" uma agência selecionada (currentAgency).
// Para não-master o backend ignora (anti-IDOR).
const sudoSelectedAgency = (getState) => {
	const app = getState().appReducer || {};
	const ca = app.currentAgency || {};
	if (app.masterUser && ca.agencyCode) {
		return { agencyId: ca.agencyCode, employeeUserId: ca.employeeCode };
	}
	return undefined;
};

const callQuote = (action, data) =>
	api.post(`/api/v1/quote/${action}`, data).then((r) => r.data);

/**
 * Voucher da reserva paga disparado PELO PAINEL da agência (BE 2026-08-07).
 *
 * Rota AUTENTICADA e por `quoteId` — não a pública por token. O motivo é auditoria, não
 * rate-limit: a rota pública grava o evento com `actor: 'cliente'`, então todo voucher
 * enviado por um operador ficaria registrado como pedido do cliente, justo no módulo onde
 * a autoria da timeline foi construída. Aqui o BE grava `actor: 'agencia'` + o UserId.
 *
 * Resposta e erros idênticos aos da rota pública (`Services/quotePublic.js`), de propósito.
 * O destinatário continua saindo do banco: o operador dispara, mas não escolhe o destino
 * nem recebe o PDF; o e-mail volta mascarado.
 */
export const enviarVoucherReserva = (quoteId, bookingId) =>
	api.post(`/api/v1/quote/${quoteId}/reserva/${bookingId}/voucher`).then((r) => r.data);

// Ponte de mock (Fase 2): filtra a lista mock pelo mesmo critério do servidor
// (status/termo) para respeitar o dropdown/busca da tela. Só usado enquanto
// !F2_BACKEND_READY. agencyId é ignorado (o mock não escopa por agência — é demo).
const filtrarMockList = (list, params = {}) => {
	let out = list || [];
	if (params.status) out = out.filter((q) => q.status === params.status);
	if (params.term) {
		const t = String(params.term).toLowerCase();
		out = out.filter((q) => {
			const campo = params.searchField === 'createdBy' ? (q.createdBy?.nome || '') : (q.nome || '');
			return String(campo).toLowerCase().includes(t);
		});
	}
	return out;
};

// Recarrega a lista (escopada pelo servidor) para o store.
const reloadList = async (dispatch, params = {}) => {
	// Enquanto o BE não expõe as cotações Fase 2, injeta as do mock (localStorage)
	// na lista para o fluxo rodar na estrutura real. Some sozinho quando
	// F2_BACKEND_READY = true (a API passa a devolver as F2). Só atrás da flag.
	const mockMode = FEATURE_FLAGS.COTACAO_FASE2 && !F2_BACKEND_READY;
	let res, apiItems = [];
	try {
		res = await callQuote('list', params);
		apiItems = (res.items || []).map(normalizeQuote);
	} catch (e) {
		if (!mockMode) throw e; // legado: propaga o erro (comportamento atual)
		// mock: não deixa a validação da Fase 2 depender da API legada de list.
	}
	const items = mockMode
		? [...filtrarMockList(mockF2.listMockQuotes(), params), ...apiItems]
		: apiItems;
	dispatch({ type: types.SET_QUOTES, payload: items });
	return res;
};

// --- Thunks -----------------------------------------------------------------

export const retrieveQuotes = (params = {}) => async (dispatch) => {
	dispatch({ type: types.SET_QUOTE_LOADING, payload: true });
	try {
		await reloadList(dispatch, params);
	} catch (e) {
		logger.error('[Quote.retrieveQuotes]', e?.response?.data || e?.message);
		throw e;
	} finally {
		dispatch({ type: types.SET_QUOTE_LOADING, payload: false });
	}
};

export const retrieveQuote = (quoteId) => async (dispatch) => {
	const res = await callQuote('retrieve', { quoteId });
	// normalizeQuoteF2 é superset do legado — traz token público, câmbio e reservas
	// enriquecidas p/ o detalhe (Fase 2) sem quebrar cotações legadas.
	const q = normalizeQuoteF2(res.quote);
	dispatch({ type: types.SET_SELECTED_QUOTE, payload: q });
	return q;
};

export const openQuoteDetail = (id) => async (dispatch) => {
	// Ponte de mock (Fase 2): a cotação F2 mock vive no localStorage (síncrono).
	// Seta o conteúdo ANTES de abrir → a modal já abre com a cotação certa (sem flash).
	if (FEATURE_FLAGS.COTACAO_FASE2 && !F2_BACKEND_READY) {
		const mockQ = mockF2.getMockQuoteById(id);
		if (mockQ) {
			dispatch({ type: types.SET_SELECTED_QUOTE, payload: mockQ });
			dispatch({ type: types.SET_QUOTE_DETAIL_OPEN, payload: true });
			return;
		}
	}
	// Real/legado (assíncrono): limpa o conteúdo antigo e abre com loading, depois
	// preenche. Assim NÃO aparece o conteúdo da cotação anterior durante a carga.
	dispatch({ type: types.SET_SELECTED_QUOTE, payload: null });
	dispatch({ type: types.SET_QUOTE_DETAIL_OPEN, payload: true });
	try {
		const res = await callQuote('retrieve', { quoteId: id });
		// normalizeQuoteF2 (superset do legado) → token público, câmbio e reservas
		// enriquecidas persistem ao abrir/recarregar; cotações legadas não são afetadas.
		dispatch({ type: types.SET_SELECTED_QUOTE, payload: normalizeQuoteF2(res.quote) });
	} catch (e) {
		logger.error('[Quote.openQuoteDetail]', e?.response?.data || e?.message);
		throw e;
	}
};

export const closeQuoteDetail = () => (dispatch) => {
	dispatch({ type: types.SET_QUOTE_DETAIL_OPEN, payload: false });
};

export const deleteQuote = (quoteId) => async (dispatch) => {
	// Ponte de mock (Fase 2): exclui do mock (localStorage) e recarrega a lista.
	if (FEATURE_FLAGS.COTACAO_FASE2 && !F2_BACKEND_READY && mockF2.getMockQuoteById(quoteId)) {
		mockF2.excluirCotacao(quoteId);
		dispatch({ type: types.SET_QUOTE_DETAIL_OPEN, payload: false });
		await reloadList(dispatch);
		return;
	}
	// Só rascunho exclui (regra do BE desde sempre; a UI se alinhou em 2026-08-07 — ver
	// `quotePodeExcluir`). O 409 QUOTE_LOCKED ainda pode chegar quando o status mudou por
	// outra via depois da tela carregar; traduz para uma frase que explica o porquê, em vez
	// do "Tente novamente" genérico — repetir não vai adiantar.
	try {
		await callQuote('delete', { quoteId });
	} catch (e) {
		const d = e?.response?.data || {};
		if ((d.error || d.code) === 'QUOTE_LOCKED') {
			await reloadList(dispatch).catch(() => {});
			const err = new Error('QUOTE_LOCKED');
			err.response = { data: { errorMessage: 'Esta cotação não está mais em rascunho e não pode ser excluída — ela é o registro da oportunidade no funil.' } };
			throw err;
		}
		throw e;
	}
	dispatch({ type: types.SET_QUOTE_DETAIL_OPEN, payload: false });
	await reloadList(dispatch);
};

// `contato` é OPCIONAL (nome/e-mail do cliente informados já na criação, só para
// localizar a cotação depois). O BE ignora enquanto não tratar o campo — ver handoff
// frontend-cotacao-fase2-ajustes-2026-08-04.
export const createQuote = (nome, { setActive = true, contato } = {}) => async (dispatch, getState) => {
	const res = await callQuote('create', { nome, contato, selectedAgency: sudoSelectedAgency(getState) });
	const q = normalizeQuote(res.quote);
	if (setActive) dispatch({ type: types.SET_ACTIVE_QUOTE, payload: q.id });
	await reloadList(dispatch);
	return q;
};

export const addItemToQuote = (quoteId, item) => async (dispatch, getState) => {
	const res = await callQuote('item/add', { quoteId, item: toApiItem(item) });
	const q = normalizeQuote(res.quote);
	dispatch({ type: types.SET_ACTIVE_QUOTE, payload: quoteId });
	if (getState().quoteReducer?.selectedQuote?.id === quoteId) {
		dispatch({ type: types.SET_SELECTED_QUOTE, payload: q });
	}
	return q;
};

export const removeQuoteItem = (quoteId, itemId) => async (dispatch, getState) => {
	const res = await callQuote('item/remove', { quoteId, itemId });
	const q = normalizeQuote(res.quote);
	if (getState().quoteReducer?.selectedQuote?.id === quoteId) {
		dispatch({ type: types.SET_SELECTED_QUOTE, payload: q });
	}
	await reloadList(dispatch);
	return q;
};

export const renameQuote = (quoteId, nome) => async (dispatch, getState) => {
	const res = await callQuote('rename', { quoteId, nome });
	const q = normalizeQuote(res.quote);
	if (getState().quoteReducer?.selectedQuote?.id === quoteId) {
		dispatch({ type: types.SET_SELECTED_QUOTE, payload: q });
	}
	await reloadList(dispatch);
	return q;
};

/**
 * Troca de status (mecanismo CRM de travar/destravar).
 * @param {object} extra - { contato } p/ 'enviada' (upsert CRM); { bookingId } p/ 'convertida'.
 */
export const setQuoteStatus = (quoteId, status, extra = {}) => async (dispatch, getState) => {
	const body = { quoteId, status };
	if (extra.contato) body.contato = extra.contato;
	if (extra.bookingId) body.bookingId = extra.bookingId;
	const res = await callQuote('status', body);
	const q = normalizeQuote(res.quote);
	if (getState().quoteReducer?.selectedQuote?.id === quoteId) {
		dispatch({ type: types.SET_SELECTED_QUOTE, payload: q });
	}
	await reloadList(dispatch);
	return q;
};

export const setActiveQuote = (id) => (dispatch) => {
	dispatch({ type: types.SET_ACTIVE_QUOTE, payload: id });
};

// "Atualizar valores e disponibilidade" — re-cota os itens (HG/PL ao vivo) + câmbio.
// Só rascunho. POST /api/v1/quote/refresh. Retorna
// { quote, resumo:{total,atualizados,indisponiveis,naoRecotaveis,cambioAtualizado,cambioUsdBrl} }.
export const refreshQuoteValues = (quoteId, opts = {}) => async (dispatch, getState) => {
	// forceUnavailable (true | [itemIds]) é só DEVEL — o BE ignora em prod (guard por ambiente).
	const body = { quoteId, ...(opts.forceUnavailable ? { forceUnavailable: opts.forceUnavailable } : {}) };
	const res = await callQuote('refresh', body);
	const q = normalizeQuote(res.quote);
	if (getState().quoteReducer?.selectedQuote?.id === quoteId) {
		dispatch({ type: types.SET_SELECTED_QUOTE, payload: q });
	}
	await reloadList(dispatch);
	return res;
};

// ===========================================================================
// COTAÇÃO FASE 2 — camada de integração (thunks) para a UI nova.
// Enquanto `F2_BACKEND_READY` = false, delega ao mock (localStorage) e despacha
// para o Redux; quando o BE subir os endpoints do handoff
// (.context/api-contracts/BACKEND-HANDOFF-cotacao-fase2.md), virar true.
//
// CHECKLIST DA VIRADA (quando o BE estiver em devel):
//  1. F2_BACKEND_READY = true (abaixo). Os thunks passam a chamar a API + normalizeQuoteF2.
//  2. Conferir normalizeQuoteF2 (acima) contra o shape real do BE — em especial o campo do
//     câmbio (`cambio` vs snapshot), os nomes das coleções (trechos/reservas/eventos) e o
//     shape de `reservas`. Ajustar os TODO(BE) se divergir.
//  3. Página pública `Screens/Public/QuoteSelection.js`: trocar `getMockQuoteByToken` pelo
//     GET público real; usar o `validoAte` do câmbio devolvido no open.
//  4. Rodar o funil ponta a ponta contra a API; então FEATURE_FLAGS.COTACAO_FASE2=true por ambiente.
// A UI já está pronta e atrás de FEATURE_FLAGS.COTACAO_FASE2.
// ===========================================================================
const F2_BACKEND_READY = true;

// Modo mock da Fase 2 = flag ligada + backend ainda não pronto. Usado pela UI
// para habilitar recursos só-de-mock (ex.: simular o pagamento no painel real).
// Vira false automaticamente quando F2_BACKEND_READY = true.
export const isF2MockMode = () => FEATURE_FLAGS.COTACAO_FASE2 && !F2_BACKEND_READY;

const setSel = (dispatch, q) => { if (q) dispatch({ type: types.SET_SELECTED_QUOTE, payload: q }); return q; };

// Busca a cotação Fase 2 canônica (retrieve) e normaliza. reservasExtra = fallback
// vindo da resposta do proceed, caso o retrieve não devolva as reservas.
const fetchQuoteF2 = async (id, reservasExtra) =>
	normalizeQuoteF2((await api.post('/api/v1/quote/retrieve', { quoteId: id })).data.quote, reservasExtra);

// f2: executa a operação e atualiza a cotação aberta + a lista (fundo + header).
//  - mock: mock() devolve a cotação completa.
//  - API real: as transições podem NÃO devolver {quote} (o proceed devolve {status,reservas}),
//    então por padrão RE-BUSCAMOS a cotação canônica (retrieve). isRetrieve=true usa a própria
//    resposta (para o próprio retrieve/refresh, que já devolve {quote}). reservasFrom(res)
//    extrai as reservas da resposta do proceed p/ usar como fallback no enriquecimento.
const f2 = async (dispatch, id, real, mock, { reload = true, isRetrieve = false, reservasFrom } = {}) => {
	try {
		let q;
		if (F2_BACKEND_READY) {
			const res = await real();
			q = isRetrieve
				? normalizeQuoteF2(res.data.quote)
				: await fetchQuoteF2(id, reservasFrom ? reservasFrom(res) : undefined);
		} else {
			q = mock();
		}
		setSel(dispatch, q);
		if (reload) await reloadList(dispatch).catch(() => {}); // não bloqueia a ação se a lista falhar
		return q;
	} catch (e) { logger.error('[Quote.F2]', e?.response?.data || e?.message); throw e; }
};

// Carrega uma cotação Fase 2 no store (abre o detalhe). Read puro — não recarrega.
export const retrieveCotacaoF2 = (id) => async (dispatch) =>
	f2(dispatch, id, () => api.post('/api/v1/quote/retrieve', { quoteId: id }), () => mockF2.getMockQuoteById(id), { reload: false, isRetrieve: true });

export const enviarCotacaoParaSelecao = (id, contato) => async (dispatch) =>
	f2(dispatch, id, () => api.post(`/api/v1/quote/${id}/send-for-selection`, { contato }), () => mockF2.enviarParaSelecao(id, contato));

export const desfazerEnvioCotacao = (id) => async (dispatch) =>
	f2(dispatch, id, () => api.post(`/api/v1/quote/${id}/undo-send`), () => mockF2.desfazerEnvio(id));

// Item 3 — avança para seleção sem disparar e-mail (agência opera pelo cliente).
export const avancarCotacaoSemEnviar = (id) => async (dispatch) =>
	f2(dispatch, id, () => api.post(`/api/v1/quote/${id}/advance-no-email`), () => mockF2.avancarSemEnviar(id));

// Item 9 — reenvia o e-mail, mantendo ou alterando o destinatário.
export const reenviarEmailCotacao = (id, email) => async (dispatch) =>
	f2(dispatch, id, () => api.post(`/api/v1/quote/${id}/resend`, { email }), () => mockF2.reenviarEmail(id, email));

// DEPRECADO (2026-08-03): a UI não expõe mais obrigatório/opcional — todo bloco é
// opcional e a regra passou a ser "ao menos uma seleção" (aprovação parcial).
// Mantido só enquanto o endpoint existir no BE; remover quando o BE aposentar o campo.
export const setTrechoCotacaoObrigatorio = (id, trechoId, obrigatorio) => async (dispatch) =>
	f2(dispatch, id, () => api.patch(`/api/v1/quote/${id}/trecho/${trechoId}`, { obrigatorio }), () => mockF2.setTrechoObrigatorio(id, trechoId, obrigatorio));

export const reabrirCotacaoParaAjuste = (id, destino) => async (dispatch) =>
	f2(dispatch, id, () => api.post(`/api/v1/quote/${id}/reopen-adjust`, { destino }), () => mockF2.reabrirParaAjuste(id, destino));

export const marcarCotacaoPerdida = (id, motivo) => async (dispatch) =>
	f2(dispatch, id, () => api.post(`/api/v1/quote/${id}/mark-lost`, { motivo }), () => mockF2.marcarPerdida(id, motivo));

export const reabrirCotacaoPerdida = (id) => async (dispatch) =>
	f2(dispatch, id, () => api.post(`/api/v1/quote/${id}/reopen`), () => mockF2.reabrirPerdida(id));

export const aprovarCotacaoComoAgencia = (id, token, selecao) => async (dispatch) =>
	f2(dispatch, id, () => api.post(`/api/v1/quote/${id}/approve-as-agency`, { selecao }), () => mockF2.aprovarSelecao(token, selecao, "agência"));

export const prosseguirComReserva = (id, dados) => async (dispatch) =>
	f2(dispatch, id, () => api.post(`/api/v1/quote/${id}/proceed-reservation`, dados), () => mockF2.prosseguirReserva(id, dados),
		{ reservasFrom: (res) => res?.data?.reservas });

// Rascunho: remove uma opção (item) da cotação Fase 2.
export const removerItemCotacao = (id, itemId) => async (dispatch) =>
	f2(dispatch, id, () => api.post('/api/v1/quote/item/remove', { quoteId: id, itemId }), () => mockF2.removerItem(id, itemId));

// Re-cota ao abrir (disponibilidade/preços/câmbio). Real reusa /quote/refresh.
// Não muda status → não recarrega a lista.
export const recotarCotacaoF2 = (id) => async (dispatch) =>
	f2(dispatch, id, () => api.post('/api/v1/quote/refresh', { quoteId: id }), () => mockF2.recotar(id), { reload: false, isRetrieve: true });

// ---------------------------------------------------------------------------
// PREVIEW DE VALORES — "ver valores de hoje" sem alterar a cotação
// ---------------------------------------------------------------------------
// Pedido do owner em 2026-09-04. A cotação aprovada mostra os valores gravados na aprovação e
// ninguém os atualiza depois: o painel só re-cota em rascunho, e o link público só em
// `aguardando_selecao`. A re-cotação ao vivo acontece dentro do proceed-reservation — ou seja,
// depois de a reserva existir. O operador decidia sem saber o preço de hoje.
//
// LEITURA PURA, obrigatoriamente. O backend já teve o bug de re-cotar fora de hora e sobrescrever
// `precoUSD/precoBRL/disponivel` de cotação encerrada (comentário em getPublicByToken). Este
// endpoint não pode gravar nada.
//
// INTEGRADO em 2026-09-05: a rota está em `main` do BE (PR #282). Verificado antes de virar —
// `scripts/quote-preview-values.test.js` presente nas três branches e a rota registrada em
// routes/index.js:307, lendo `req.params.id` (que é como chamamos, sem body).
// Contrato: .context/api-contracts/FRONTEND-preview-de-valores-2026-09-04.md
// Correção do escopo: FRONTEND-preview-o-cambio-ja-era-vivo-2026-09-05.md
const PREVIEW_BACKEND_READY = true;

// O botao NAO pode aparecer em producao enquanto isto for mock: ele exibe VALOR FINANCEIRO, e um
// numero inventado numa tela de cotacao e pior do que numero nenhum — o operador decidiria reserva
// em cima de ficcao. Em devel/staging fica visivel para dar para testar a UI.
export const previewValoresDisponivel = () => PREVIEW_BACKEND_READY || env.APP_ENV !== "production";

export const previewValoresCotacao = (id) => async (dispatch, getState) => {
	// No mock a fonte e a cotacao ABERTA no store — nao a lista semeada do localStorage, que nao
	// contem as cotacoes vindas da API real.
	if (!PREVIEW_BACKEND_READY) return mockF2.previewValores(getState()?.quoteReducer?.selectedQuote);
	const res = await api.post(`/api/v1/quote/${id}/preview-values`);
	return res.data;
};

// dev-only (sem backend): simula o retorno de pagamento do módulo de reservas.
// Muda o status derivado → recarrega a lista para refletir ao fundo e no header.
export const simularStatusReservaCotacao = (id, reservaId, status) => async (dispatch) => {
	const q = setSel(dispatch, mockF2.setReservaStatus(id, reservaId, status));
	await reloadList(dispatch).catch(() => {});
	return q;
};
